import React from 'react';
import { useReturnStore } from '@src/stores/returnStore';
import {
  MAX_WEIGHT_LIMIT,
  RETURN_METHOD,
  ReturnMethodType,
} from '@src/constants/general';
import { SelectedCardType } from '@src/models/returns';
import { getFeatureSwitchStatic, getTotalWeight } from '@src/utils/general';
import CardMethod from './CardMethod';
import PostSelectionItems from '../shared/items/PostSelectionItems';
import CardMethodDescription from './CardMethodDescription';

interface OrderReturnMethodOptionsProps {
  selectedCard: SelectedCardType;
  setSelectedCard: React.Dispatch<React.SetStateAction<SelectedCardType>>;
}

const headerMap: { [key in ReturnMethodType]: string } = {
  'In-Store': 'In-Store Return',
  UPS: 'UPS Drop-Off',
  LORP: 'Return Pickup',
  SORIS: 'In-Store Return',
  APO_FPO: 'Military Post Office Drop-Off',
};

const OrderReturnMethodOptions = ({
  selectedCard,
  setSelectedCard,
}: OrderReturnMethodOptionsProps) => {
  const selectedItems = useReturnStore((state) => state.selectedItems);
  const handleMethodClick = (method: SelectedCardType) => {
    setSelectedCard(method);
  };

  const showApoFpoOption = selectedItems.every(
    (item) => item.eligibilities?.APO_FPO,
  );

  // APO/FPO and UPS are mutually exclusive
  const showUPSOption =
    selectedItems.every((item) => item.eligibilities?.UPS) &&
    getTotalWeight(selectedItems) < MAX_WEIGHT_LIMIT &&
    !showApoFpoOption;

  const showLORPOption = selectedItems.every(
    (item) => item.eligibilities?.LORP,
  );
  const showSplitOption =
    !showApoFpoOption &&
    selectedItems.some(
      (item) => item.eligibilities?.UPS || item.eligibilities?.LORP,
    );

  const showSORISOption =
    getFeatureSwitchStatic('allowSORIS') &&
    selectedItems.every((item) => item.eligibilities?.SORIS);

  // TODO : When SORIS is live, we need to make sure In-Store option shows up only when some items or all items are not SORIS eligible and conditionally render In-Store option
  return (
    <>
      <PostSelectionItems selectedReturnItems={selectedItems} />

      <div
        data-testid="order-return-method-options"
        className="sui-flex sui-flex-col sui-gap-4"
      >
        {showApoFpoOption && (
          <CardMethod
            header={headerMap.APO_FPO}
            onClick={() => handleMethodClick(RETURN_METHOD.APO_FPO)}
            selected={selectedCard === RETURN_METHOD.APO_FPO}
            returnCost="TBD"
          >
            <CardMethodDescription method={RETURN_METHOD.APO_FPO} />
          </CardMethod>
        )}
        {!showSORISOption && (
          <CardMethod
            header={headerMap['In-Store']}
            onClick={() => handleMethodClick('In-Store')}
            selected={selectedCard === 'In-Store'}
          >
            <CardMethodDescription method="In-Store" />
          </CardMethod>
        )}
        {showSORISOption && (
          <CardMethod
            header={headerMap.SORIS}
            onClick={() => handleMethodClick('SORIS')}
            selected={selectedCard === 'SORIS'}
          >
            <CardMethodDescription method="SORIS" />
          </CardMethod>
        )}
        {showUPSOption && (
          <CardMethod
            header={headerMap.UPS}
            onClick={() => handleMethodClick('UPS')}
            selected={selectedCard === 'UPS'}
          >
            <CardMethodDescription method="UPS" />
          </CardMethod>
        )}
        {showLORPOption && (
          <CardMethod
            header={headerMap.LORP}
            onClick={() => handleMethodClick('LORP')}
            selected={selectedCard === 'LORP'}
          >
            <CardMethodDescription method="LORP" />
          </CardMethod>
        )}
        {showSplitOption && (
          <CardMethod
            header="Select Per Item"
            onClick={() => handleMethodClick('Split')}
            selected={selectedCard === 'Split'}
          >
            <CardMethodDescription method="Split" />
          </CardMethod>
        )}
      </div>
    </>
  );
};

export default OrderReturnMethodOptions;
